import React, { useState, useEffect, startTransition, useCallback, useMemo, memo, useDeferredValue, useRef, Suspense, lazy } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import toast from 'react-hot-toast';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  Tooltip,
  TextField,
  Autocomplete,
  ListSubheader,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ViewModule as ViewModuleIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  ArrowDropDown as ArrowDropDownIcon,
  OpenInFull as OpenInFullIcon,
  CloseFullscreen as CloseFullscreenIcon,
  CompareArrows as CompareArrowsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// Import components
import SpreadMatrix from '../components/SpreadMatrix';
import VirtualizedSpreadMatrix from '../components/VirtualizedSpreadMatrix';
import BurgerMenu from '../components/BurgerMenu';
import TickerTape from '../components/TickerTape';
import ListsPanel from '../components/ListsPanel';
import ListEditDialogs from '../components/ListEditDialogs';
import useListEditing from '../hooks/useListEditing';
import { TYPE_FILTERS, loadTypeFilters, saveTypeFilters, applyTypeFilters, activeFlags, countByType } from '../utils/typeFilter';
import axios from 'axios';

// Import store actions
import { fetchUserLists } from '../store/listsSlice';

// Import Redux actions for real data
import { fetchSpreads, fetchMatrixWithLevels, fetchMedPercReference, reanalyzeSpreads, setGranularity, setLookbackDays, checkDataFreshness, syncData, fetchSyncStatus, syncFinished, clearClientSpreadCaches, CURVE_FIELD_BY_SPREAD_TYPE } from '../store/spreadsSlice';
import { useWebSocket } from '../hooks/useWebSocket';
import useMarketSession from '../hooks/useMarketSession';
import { isSupersededStream, priceBasis, accrualForStream, cleanPriceFromStream, officialAheadOfPayload, officialRereadDelayMs, CLOSE_PRINT_GRACE_MS } from '../utils/session';
import { getApiUrl, addUserParam } from '../config/api.config';
import { fetchSubscriptions } from '../store/alertsSlice';

// Lazy load AllChartsPanel - only loaded when user clicks a cell (saves ~300KB initial bundle)
const AllChartsPanel = lazy(() => import('../components/AllChartsPanel'));
const SectorComparison = lazy(() => import('../components/SectorComparison'));

// Lists longer than this render with the virtualized grid, which mounts only the
// visible cells. The full table at 98 tickers is 48,578 DOM elements against
// ~1,145 for the grid; below ~40 tickers the table is small enough either way.
const VIRTUALIZATION_THRESHOLD = 40;

// How many matrices can be open at once (3 x 3).
const MAX_BOXES = 9;

// LRU Cache configuration for prefetch - prevents unbounded memory growth
const PREFETCH_CACHE_MAX_SIZE = 50;

// LRU cache helper - evicts oldest entries when size exceeds max
const addToLRUCache = (cache, key, value, maxSize = PREFETCH_CACHE_MAX_SIZE) => {
  // If key exists, update it (move to "newest" by deleting and re-adding)
  if (cache[key]) {
    delete cache[key];
  }

  // Check if we need to evict oldest entries
  const keys = Object.keys(cache);
  if (keys.length >= maxSize) {
    // Find and remove oldest entries (those with smallest timestamps or first in object)
    // Sort by timestamp and remove oldest 10%
    const sortedKeys = keys
      .filter(k => cache[k]?.timestamp)
      .sort((a, b) => (cache[a].timestamp || 0) - (cache[b].timestamp || 0));

    const toRemove = Math.max(1, Math.floor(maxSize * 0.1)); // Remove 10% or at least 1
    for (let i = 0; i < toRemove && i < sortedKeys.length; i++) {
      delete cache[sortedKeys[i]];
    }
  }

  // Add new entry
  cache[key] = value;
};

// Helper to invert historical spread data (for B-A when cached as A-B)
const invertHistoricalData = (data) => {
  if (!data || !Array.isArray(data)) return data;
  return data.map(point => ({
    ...point,
    dollarSpread: point.dollarSpread !== undefined ? -point.dollarSpread : undefined,
    yieldSpread: point.yieldSpread !== undefined ? -point.yieldSpread : undefined,
    ytcSpread: point.ytcSpread !== undefined ? -point.ytcSpread : undefined,
    ytmSpread: point.ytmSpread !== undefined ? -point.ytmSpread : undefined,
    spotYieldSpread: point.spotYieldSpread !== undefined ? -point.spotYieldSpread : undefined,
    // Swap ticker1/ticker2 data
    ticker1Price: point.ticker2Price,
    ticker1Yield: point.ticker2Yield,
    ticker1YTC: point.ticker2YTC,
    ticker1SpotYield: point.ticker2SpotYield,
    ticker2Price: point.ticker1Price,
    ticker2Yield: point.ticker1Yield,
    ticker2YTC: point.ticker1YTC,
    ticker2SpotYield: point.ticker1SpotYield,
  }));
};

const scalingSig = (sf) => Object.keys(sf).sort().map(k => `${k}=${sf[k]}`).join('|');

// A small integer per price kind, folded into the live-price signature so a
// change of kind alone (the session turned over, the numbers did not) still
// publishes new prices.
const kindCode = (kind) => (kind ? kind.length * 31 + kind.charCodeAt(0) : 0);

// Get sorted cache key for pair (canonical ordering, includes lookback days)
const getSortedCacheKey = (ticker1, ticker2, granularity, lookbackDays) => {
  const [t1, t2] = [ticker1, ticker2].sort();
  return `${t1}:${t2}:${granularity}:${lookbackDays}d`;
};

// Chart dialog time range -> lookback days (the matrix uses the same three).
const TIME_RANGE_DAYS = { '3M': 90, '6M': 180, '1Y': 365 };

// Δ Med% comparison: sessions back from the displayed one.
const DELTA_OPTIONS = [
  { value: 1, label: '1D' },
  { value: 5, label: '1W' },
  { value: 21, label: '1M' },
];
// The reference close is fixed for the day, but the "current" session rolls at
// the 09:30 open; re-asking this often catches that (server answers from cache).
const MED_REF_REFRESH_MS = 15 * 60 * 1000;
// How often a running Sync Spreads job is polled for progress (a hidden tab
// only needs to notice that it ended).
const SYNC_POLL_MS = 5000;
const SYNC_POLL_HIDDEN_MS = 30000;
// Who started a sync, in words. The backend names a run it did not start
// 'scheduler' (the nightly run) or 'orphaned run' (its parent has exited).
const syncStarter = (startedBy) => (
  startedBy === 'scheduler' ? 'the nightly scheduler'
    : startedBy === 'orphaned run' ? 'a detached process'
      : startedBy
);

// Convert lookback days to time range string for dialog
const lookbackDaysToTimeRange = (days) => {
  if (days <= 90) return '3M';
  if (days <= 180) return '6M';
  return '1Y';
};

// Matrix Box component - each box has its own list selector
const MatrixBox = memo(({
  boxId,
  selectedListName,
  availableLists,
  defaultListNames = [],
  onListChange,
  onRemove,
  onReanalyze,
  spreadType,
  livePrices,
  matrixData,
  matrixYields,
  listTickers,
  isLoading,
  isReanalyzing,
  granularity,
  onCellClick,
  onCellHover,
  onCellHoverEnd,
  boxHeight = 550,
  getScaleFactor = null,
  boxNumber = 1,
  isActive = false,
  onActivate,
  isMaximized = false,
  onToggleMaximize,
  referenceMedPerc = null,
  referenceSession = null,
  onRemoveTickers = null,   // (listName, tickers[]) — right-click a ticker: remove it from the list
  onMoveTickers = null,     // (listName, tickers[]) — right-click a ticker: move it to another list
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Search state for this matrix box
  const [searchRow, setSearchRow] = useState(null);
  const [searchCol, setSearchCol] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Right-click on a row's ticker: edit the list from the matrix itself.
  const [tickerMenu, setTickerMenu] = useState(null);   // { x, y, ticker }

  return (
    <Paper
      elevation={2}
      // Capture phase so a click anywhere in the box (including a matrix cell)
      // makes it the target of the lists panel before the click is handled.
      onMouseDownCapture={() => onActivate && onActivate(boxId)}
      sx={{
        p: 2,
        height: boxHeight,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        outline: isActive ? `2px solid ${theme.palette.primary.main}` : 'none',
        outlineOffset: -2,
        minWidth: 0,
        // Container queries: the header reacts to the BOX's width, not the window's.
        containerType: 'inline-size',
        containerName: 'matrixbox',
        // Wide: search inline, no toggle. Narrow: search folds behind the toggle
        // (and opens on its own line), and "Sync List" keeps only its icon.
        '& .mb-search-toggle': { display: 'none' },
        '@container matrixbox (max-width: 560px)': {
          '& .mb-search-toggle': { display: 'inline-flex' },
          '& .mb-search': { display: 'none', order: 3, flex: '1 0 100%' },
          '& .mb-search.mb-search-open': { display: 'flex' },
          '& .mb-sync-label': { display: 'none' },
        },
      }}
    >
      {/* Header with dropdown and controls - compact.
          It wraps instead of overflowing, and the action buttons never shrink, so
          Maximize and Close are always reachable however small the matrix is
          (9 matrices in a 3 x 3 grid are ~400px wide). Below ~560px the Row/Col
          search folds behind a search button and "Sync List" keeps its icon only. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 0.5,
          rowGap: 0.25,
          mb: 0.5,
          minHeight: 32,
          flexShrink: 0,
        }}
      >
        {/* Badge + list selector */}
        <Box display="flex" alignItems="center" gap={0.5} sx={{ flex: '1 1 0', minWidth: 96, order: 1 }}>
          <Tooltip title={isActive ? 'Active matrix — lists picked in the side panel load here' : 'Click to make this the active matrix'}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                px: 0.5,
                borderRadius: 0.5,
                flexShrink: 0,
                color: isActive ? 'primary.contrastText' : 'text.secondary',
                backgroundColor: isActive ? 'primary.main' : 'action.selected',
              }}
            >
              #{boxNumber}
            </Typography>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 0, flex: '0 1 220px', maxWidth: '100%' }}>
            <Select
              value={selectedListName || ''}
              onChange={(e) => onListChange(boxId, e.target.value)}
              displayEmpty
              IconComponent={ExpandMoreIcon}
              sx={{
                fontSize: '0.75rem',
                minWidth: 0,
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                <em>Select list...</em>
              </MenuItem>
            {/* Own lists, then GICS sector defaults. Select needs flat children,
                hence the array rather than a fragment. */}
            {[
              { title: 'My lists', names: Object.keys(availableLists).filter(n => !defaultListNames.includes(n)).sort((a, b) => a.localeCompare(b)) },
              { title: 'Sector lists · GICS', names: Object.keys(availableLists).filter(n => defaultListNames.includes(n)).sort((a, b) => a.localeCompare(b)) },
            ].filter(g => g.names.length > 0).flatMap(g => [
              <ListSubheader key={`h-${g.title}`} sx={{ fontSize: '0.7rem', lineHeight: '26px' }}>{g.title}</ListSubheader>,
              ...g.names.map((listName) => (
                <MenuItem key={listName} value={listName} sx={{ fontSize: '0.75rem' }}>
                  {listName} ({availableLists[listName]?.length || 0})
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>
        </Box>

        {/* Search boxes - only show when a list is selected - compact */}
        {selectedListName && listTickers.length > 0 && (
          <Box
            className={`mb-search${searchOpen ? ' mb-search-open' : ''}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 1 auto', minWidth: 0, order: 2 }}
          >
            <Autocomplete
              size="small"
              options={listTickers}
              value={searchRow}
              onChange={(_, val) => setSearchRow(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Row"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.25 } }}
                />
              )}
              sx={{
                width: 130,
                minWidth: 80,
                '& .MuiInputBase-root': { py: 0, minHeight: 28 },
                '& .MuiAutocomplete-input': { fontSize: '0.7rem' }
              }}
              disableClearable={false}
            />
            <Autocomplete
              size="small"
              options={listTickers}
              value={searchCol}
              onChange={(_, val) => setSearchCol(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Col"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.25 } }}
                />
              )}
              sx={{
                width: 130,
                minWidth: 80,
                '& .MuiInputBase-root': { py: 0, minHeight: 28 },
                '& .MuiAutocomplete-input': { fontSize: '0.7rem' }
              }}
              disableClearable={false}
            />
          </Box>
        )}

        {/* Action buttons: never shrink, always last on the first line */}
        <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0, order: 1, ml: 'auto' }}>
          {selectedListName && listTickers.length > 0 && (
            <Tooltip title="Search a row or column">
              <IconButton
                className="mb-search-toggle"
                size="small"
                onClick={() => setSearchOpen(o => !o)}
                aria-label="Toggle row and column search"
                sx={{ color: searchOpen || searchRow || searchCol ? 'primary.main' : 'text.secondary' }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {selectedListName && (
            <Tooltip title={isReanalyzing ? 'Syncing…' : 'Sync List'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isReanalyzing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}
                  onClick={() => onReanalyze(selectedListName)}
                  disabled={isReanalyzing}
                  aria-label="Sync List"
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    fontSize: '0.75rem',
                    '& .MuiButton-startIcon': { mr: { xs: 0 }, ml: 0 },
                  }}
                >
                  <span className="mb-sync-label" style={{ marginLeft: 6 }}>{isReanalyzing ? 'Syncing...' : 'Sync List'}</span>
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isMaximized ? 'Restore (Esc)' : 'Maximize'}>
            <IconButton
              size="small"
              onClick={() => onToggleMaximize && onToggleMaximize(boxId)}
              aria-label={isMaximized ? 'Restore matrix' : 'Maximize matrix'}
              sx={{ color: isMaximized ? 'primary.main' : 'text.secondary' }}
            >
              {isMaximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => onRemove(boxId)}
            aria-label="Close matrix"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'error.lighter',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Matrix content area */}
      <Box
        sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
        onContextMenu={(e) => {
          if (!selectedListName || !onRemoveTickers) return;
          const el = e.target.closest ? e.target.closest('[data-ticker-header]') : null;
          if (!el) return;
          e.preventDefault();
          setTickerMenu({ x: e.clientX, y: e.clientY, ticker: el.getAttribute('data-ticker-header') });
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'rgba(26,26,26,0.8)' : 'rgba(255,255,255,0.7)',
              zIndex: 10,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {!selectedListName ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'grey.900' : 'grey.50',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select a list to display spread matrix
            </Typography>
          </Box>
        ) : listTickers.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'grey.900' : 'grey.50',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              List is empty
            </Typography>
          </Box>
        ) : listTickers.length > VIRTUALIZATION_THRESHOLD ? (
          // Use virtualized matrix for large lists (>100 tickers)
          <VirtualizedSpreadMatrix
            tickers={listTickers}
            matrix={matrixData || {}}
            yields={matrixYields || null}
            granularity={granularity}
            spreadType={spreadType}
            livePrices={livePrices}
            onCellClick={(ticker1, ticker2) => onCellClick(ticker1, ticker2, selectedListName)}
            searchRow={searchRow}
            searchCol={searchCol}
            getScaleFactor={getScaleFactor}
            referenceMedPerc={referenceMedPerc}
            referenceSession={referenceSession}
          />
        ) : (
          // Use regular matrix for smaller lists
          <SpreadMatrix
            tickers={listTickers}
            matrix={matrixData || {}}
            yields={matrixYields || null}
            granularity={granularity}
            spreadType={spreadType}
            livePrices={livePrices}
            onCellClick={(ticker1, ticker2) => onCellClick(ticker1, ticker2, selectedListName)}
            onCellHover={onCellHover}
            onCellHoverEnd={onCellHoverEnd}
            searchRow={searchRow}
            searchCol={searchCol}
            getScaleFactor={getScaleFactor}
            referenceMedPerc={referenceMedPerc}
            referenceSession={referenceSession}
          />
        )}
      </Box>

      <Menu
        open={!!tickerMenu}
        onClose={() => setTickerMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={tickerMenu ? { top: tickerMenu.y, left: tickerMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem disabled sx={{ fontSize: '0.75rem', opacity: '1 !important', fontWeight: 700 }}>
          {tickerMenu?.ticker} · in “{selectedListName}”
        </MenuItem>
        <MenuItem
          sx={{ fontSize: '0.8rem' }}
          onClick={() => { const t = tickerMenu.ticker; setTickerMenu(null); onMoveTickers && onMoveTickers(selectedListName, [t]); }}
        >
          Move to another list…
        </MenuItem>
        <MenuItem
          sx={{ fontSize: '0.8rem', color: 'error.main' }}
          onClick={() => { const t = tickerMenu.ticker; setTickerMenu(null); onRemoveTickers && onRemoveTickers(selectedListName, [t]); }}
        >
          Remove from this list
        </MenuItem>
      </Menu>
    </Paper>
  );
});

// Throttle helper - only update if enough time has passed
const useThrottledValue = (value, delay = 1000) => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastUpdateRef = useRef(Date.now());
  const pendingValueRef = useRef(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    pendingValueRef.current = value;
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate >= delay) {
      // Enough time passed, update immediately
      setThrottledValue(value);
      lastUpdateRef.current = now;
    } else if (!timeoutRef.current) {
      // Schedule update for remaining time
      timeoutRef.current = setTimeout(() => {
        setThrottledValue(pendingValueRef.current);
        lastUpdateRef.current = Date.now();
        timeoutRef.current = null;
      }, delay - timeSinceLastUpdate);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, delay]);

  return throttledValue;
};

// ============================================
// Dashboard state persistence helpers
// ============================================
const DASHBOARD_STATE_KEY = 'dashboard_state';

const loadDashboardState = () => {
  try {
    const username = localStorage.getItem('username') || 'default';
    const raw = localStorage.getItem(`${DASHBOARD_STATE_KEY}_${username}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Validate basic structure to avoid crashes from stale data
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch (e) {
    // Corrupted state — clear it
    try {
      const username = localStorage.getItem('username') || 'default';
      localStorage.removeItem(`${DASHBOARD_STATE_KEY}_${username}`);
    } catch (_) {}
  }
  return null;
};

const saveDashboardState = (state) => {
  try {
    const username = localStorage.getItem('username') || 'default';
    localStorage.setItem(`${DASHBOARD_STATE_KEY}_${username}`, JSON.stringify(state));
  } catch (e) { /* ignore quota errors */ }
};

const Dashboard = () => {
  const dispatch = useDispatch();
  // Use shallowEqual to prevent re-renders when object reference changes but content is same
  const selectedGranularity = useSelector((state) => state.spreads.selectedGranularity);
  const selectedLookbackDays = useSelector((state) => state.spreads.selectedLookbackDays);
  const matrixWithLevels = useSelector((state) => state.spreads.matrixWithLevels, shallowEqual);
  const yields = useSelector((state) => state.spreads.yields, shallowEqual);
  // The lists as stored (what the side panel edits) and as the matrices see them:
  // the same lists minus the tickers a type filter (Convertible / Fixed to Floater /
  // Variable Rate) hides. A filter is a view: nothing is removed from the stored
  // lists, so switching it off restores every list exactly as it was.
  const storedLists = useSelector((state) => state.lists.lists, shallowEqual);
  const [typeFilters, setTypeFilters] = useState(loadTypeFilters);
  const [tickerTypes, setTickerTypes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${getApiUrl()}/tickers/types`, { withCredentials: true, timeout: 15000 })
      .then((r) => { if (!cancelled) setTickerTypes(r.data?.types || {}); })
      .catch(() => { /* filters stay inert until the flags load */ });
    return () => { cancelled = true; };
  }, []);
  const lists = useMemo(
    () => applyTypeFilters(storedLists, tickerTypes, typeFilters),
    [storedLists, tickerTypes, typeFilters]
  );
  const excludedFlags = activeFlags(typeFilters);
  const typeCounts = useMemo(() => countByType(storedLists, tickerTypes), [storedLists, tickerTypes]);
  const handleTypeFilter = useCallback((key, value) => {
    setTypeFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveTypeFilters(next);
      return next;
    });
  }, []);
  const defaultListNames = useSelector((state) => state.lists.defaultListNames, shallowEqual);
  // Get live prices from tickerTape (WebSocket updates from live_data_today)
  const wsPrices = useSelector((state) => state.tickerTape.prices, shallowEqual);

  // Market session (ET) and the end of the regular one. When it ends, a tab that
  // was streaming trades re-reads its matrices the way a fresh load would, so
  // the last trade gives way to the close instead of staying until a reload.
  // Matrix payloads are cached server-side for 60 s, so the re-read waits that
  // out (plus the closing print) and is jittered so open tabs do not all land
  // at once. Until it arrives the last trade stays on screen: one swap, no gap.
  const session = useMarketSession();
  const [rthEndedAt, setRthEndedAt] = useState(null);
  const [sessionRefresh, setSessionRefresh] = useState(0);
  const prevSessionRef = useRef(session);
  useEffect(() => {
    const prev = prevSessionRef.current;
    prevSessionRef.current = session;
    if (prev === session) return undefined;
    // A cached payload was priced in the session that just ended.
    clearClientSpreadCaches();
    if (prev !== 'regular') return undefined;
    // Never reset on the next open: trades streamed yesterday must stay
    // superseded then, and today's (received later) win by their own stamp.
    setRthEndedAt(Date.now());
    const timer = setTimeout(() => {
      clearClientSpreadCaches();
      setSessionRefresh((n) => n + 1);
    }, 60 * 1000 + CLOSE_PRINT_GRACE_MS + Math.random() * 25 * 1000);
    return () => clearTimeout(timer);
  }, [session]);
  // Data sync state
  const dataFreshness = useSelector((state) => state.spreads.dataFreshness);
  const dataSyncing = useSelector((state) => state.spreads.dataSyncing);
  const dataSyncError = useSelector((state) => state.spreads.dataSyncError);
  const syncJob = useSelector((state) => state.spreads.syncJob);

  // Restore saved state on first render
  const savedState = useRef(loadDashboardState());

  // State for spread type selection - use startTransition for instant button response
  const [spreadType, setSpreadType] = useState(
    () => savedState.current?.spreadType || 'dollar'
  );

  // State for matrix boxes - each box has its own selected list
  // Format: [{ id: 1, listName: 'banks' }, { id: 2, listName: 'sama' }, ...]
  const [matrixBoxes, setMatrixBoxes] = useState(
    () => savedState.current?.matrixBoxes || [{ id: 1, listName: null }]
  );
  const [nextBoxId, setNextBoxId] = useState(
    () => {
      const saved = savedState.current?.matrixBoxes;
      if (saved && saved.length > 0) {
        return Math.max(...saved.map(b => b.id)) + 1;
      }
      return 2;
    }
  );

  // Box that the lists panel loads into; highlighted with a border.
  const [activeBoxId, setActiveBoxId] = useState(
    () => savedState.current?.activeBoxId ?? savedState.current?.matrixBoxes?.[0]?.id ?? 1
  );
  // One box shown alone at full size; the others stay mounted (search, sort and
  // scroll survive) but hidden, with their live prices frozen so they do no work.
  const [maximizedBoxId, setMaximizedBoxId] = useState(null);
  const frozenLivePricesRef = useRef(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(
    () => savedState.current?.sidePanelOpen ?? true
  );
  const [deltaSessionsBack, setDeltaSessionsBack] = useState(
    () => savedState.current?.deltaSessionsBack ?? 1
  );

  // Restore Redux state (granularity, lookback) from saved state on mount
  useEffect(() => {
    const saved = savedState.current;
    if (saved) {
      if (saved.granularity) dispatch(setGranularity(saved.granularity));
      if (saved.lookbackDays) dispatch(setLookbackDays(saved.lookbackDays));
    }
  }, [dispatch]);

  // Per-pair dollar spread scaling factors: { "TICKER1|TICKER2": 1.5, ... }
  // Persisted in localStorage per user. Key is sorted alphabetically.
  const [scalingFactors, setScalingFactors] = useState(
    () => savedState.current?.scalingFactors || {}
  );

  const getScaleFactor = useCallback((t1, t2) => {
    const key = [t1, t2].sort().join('|');
    return scalingFactors[key] ?? 1;
  }, [scalingFactors]);

  const setScaleFactor = useCallback((t1, t2, s) => {
    const key = [t1, t2].sort().join('|');
    setScalingFactors(prev => {
      // Unchanged (e.g. focusing and leaving the ratio box) must keep the same
      // object, or every open matrix refetches. 1 is the default: drop the key.
      if ((prev[key] ?? 1) === s) return prev;
      const next = { ...prev };
      if (s === 1) delete next[key]; else next[key] = s;
      return next;
    });
  }, []);

  // Persist dashboard state to localStorage on changes
  useEffect(() => {
    saveDashboardState({
      spreadType,
      matrixBoxes,
      granularity: selectedGranularity,
      lookbackDays: selectedLookbackDays,
      scalingFactors,
      activeBoxId,
      sidePanelOpen,
      deltaSessionsBack,
    });
  }, [spreadType, matrixBoxes, selectedGranularity, selectedLookbackDays, scalingFactors, activeBoxId, sidePanelOpen, deltaSessionsBack]);

  // Mirror scalingFactors to backend so the alertChecker process (running
  // independently of the web backend) always uses the user's current ratios.
  // Fire-and-forget; failures are non-fatal — alerts that haven't yet seen
  // the new ratio just keep using the stored one until the next change.
  useEffect(() => {
    fetch(addUserParam(`${getApiUrl()}/alerts/sync-state`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ scalingFactors }),
    }).catch(() => { /* alerts may be unconfigured; ignore */ });
  }, [scalingFactors]);

  // Track loading state per list
  const [loadingLists, setLoadingLists] = useState({});

  // State for reanalyze feedback
  const [reanalyzeListName, setReanalyzeListName] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Get unique list names from boxes
  const activeListNames = React.useMemo(() => {
    return matrixBoxes
      .map(box => box.listName)
      .filter(name => name !== null);
  }, [matrixBoxes]);

  // Get spread matrices from Redux store (real data from MongoDB)
  const spreadMatrices = React.useMemo(() => {
    const matrices = {};

    activeListNames.forEach(listName => {
      const matrixData = matrixWithLevels[listName];
      if (matrixData && matrixData.matrix) {
        matrices[listName] = matrixData.matrix;
      }
    });

    return matrices;
  }, [activeListNames, matrixWithLevels]);

  // Per-list per-ticker yields, which carry the accrued dividend the Strip
  // column needs to turn a live price into a live clean price.
  const spreadYields = React.useMemo(() => {
    const out = {};
    activeListNames.forEach(listName => {
      const md = matrixWithLevels[listName];
      if (md && md.yields) out[listName] = md.yields;
    });
    return out;
  }, [activeListNames, matrixWithLevels]);

  // Track previous yield hash to avoid unnecessary re-renders
  const prevYieldSignatureRef = useRef(0);
  const cachedLivePricesRef = useRef({});

  // Get live prices - merge WebSocket prices (from live_data_today) with yield data (from ClickHouse)
  // Priority: WebSocket price > yields price (WebSocket is real-time from MongoDB)
  // Optimized: Use numeric hash instead of string concatenation for signature
  const livePrices = React.useMemo(() => {
    const prices = {};
    let hash = 0; // Simple numeric hash for yield-relevant fields

    // First, add yield data from ClickHouse (has YTM, YTC, etc.)
    const yieldKeys = Object.keys(yields);
    for (let i = 0; i < yieldKeys.length; i++) {
      const ticker = yieldKeys[i];
      const yieldData = yields[ticker];
      if (yieldData) {
        const ytm = yieldData.ytm || 0;
        const ytc = yieldData.ytc || 0;
        const price = yieldData.price || 0;
        prices[ticker] = {
          price: price,
          cleanPrice: yieldData.clean_price || 0,
          currentYield: yieldData.current_yield || 0,
          ytm: ytm,
          ytc: ytc,
          ytw: yieldData.ytw || 0,
          spotYield: yieldData.spot_yield || 0,
          dividend: yieldData.dividend || 0,
          accrual: yieldData.accrual || 0,
          // What the number is (kind: last / prev close / close): the server's label
          // when the payload has one, else the session it was fetched in.
          kind: priceBasis(yieldData.session, yieldData.fs),
          kindDate: yieldData.sessionDate || null,
        };
        // Accumulate hash from yield-relevant fields (faster than string concat)
        hash = ((hash << 5) - hash + Math.round(ytm * 10000)) | 0;
        hash = ((hash << 5) - hash + Math.round(ytc * 10000)) | 0;
        hash = ((hash << 5) - hash + Math.round(price * 100)) | 0;
        hash = ((hash << 5) - hash + kindCode(prices[ticker].kind)) | 0;
      }
    }

    // Then, overlay with WebSocket live prices (mutate in place, no spread)
    const wsKeys = Object.keys(wsPrices);
    for (let i = 0; i < wsKeys.length; i++) {
      const ticker = wsKeys[i];
      const wsData = wsPrices[ticker];
      if (wsData && wsData.price) {
        // A streamed trade from a regular session that has ended gives way to a
        // payload fetched after it (the close). Until one exists it stays.
        if (isSupersededStream(wsData, yields[ticker], rthEndedAt)) continue;
        const existing = prices[ticker] || {};
        // Mutate existing object or create minimal new one
        prices[ticker] = existing;
        const wsPrice = wsData.price;
        existing.price = wsPrice;
        existing.change = wsData.change || 0;
        existing.changePercent = wsData.changePercent || 0;
        existing.bid = wsData.bid;
        existing.ask = wsData.ask;
        existing.volume = wsData.volume;
        existing.kind = priceBasis(wsData.session, wsData.rs);
        existing.kindDate = wsData.sessionDate || null;
        // Override yield data with WebSocket values when available (live > stale ClickHouse)
        // Use !== undefined/null check (not truthiness) so 0 values still override
        if (wsData.currentYield != null) existing.currentYield = wsData.currentYield;
        if (wsData.ytm != null) existing.ytm = wsData.ytm;
        if (wsData.ytc != null) existing.ytc = wsData.ytc;
        if (wsData.ytw != null) existing.ytw = wsData.ytw;
        if (wsData.spotYield != null) existing.spotYield = wsData.spotYield;
        if (wsData.cleanPrice != null) existing.cleanPrice = wsData.cleanPrice;
        if (wsData.dividend != null) existing.dividend = wsData.dividend;
        if (wsData.accrual != null) existing.accrual = wsData.accrual;
        else if (yields[ticker]) {
          // A live trade is paired with today's accrual even when the payload (built before the
          // open) paired its own, previous-close price with the previous session's row.
          const acc = accrualForStream(yields[ticker], wsData);
          if (acc != null) existing.accrual = acc;
        }
        // Strip = clean price = live price − accrued dividend.
        // The yields row gave us a stale snapshot of clean_price (computed at
        // last calculator run); recompute here so Strip moves with the live
        // tick. Skip for tickers without an accrual (DATCO etc.) so cleanPrice
        // stays at its stored value (0 for price-only tickers → renders as
        // "-" in the Strip column).
        const clean = cleanPriceFromStream(yields[ticker], existing.accrual, wsPrice);
        if (clean != null) existing.cleanPrice = clean;
        // Update hash with WS price AND change data (for live updates)
        hash = ((hash << 5) - hash + Math.round(wsPrice * 100)) | 0;
        hash = ((hash << 5) - hash + Math.round((wsData.change || 0) * 10000)) | 0;
        hash = ((hash << 5) - hash + Math.round((existing.cleanPrice || 0) * 100)) | 0;
        hash = ((hash << 5) - hash + kindCode(existing.kind)) | 0;
      }
    }

    // If hash unchanged, return cached object (prevents child re-renders)
    if (hash === prevYieldSignatureRef.current) {
      return cachedLivePricesRef.current;
    }

    // Yield data changed - update cache and hash
    prevYieldSignatureRef.current = hash;
    cachedLivePricesRef.current = prices;
    return prices;
  }, [yields, wsPrices, rthEndedAt]);

  // The official close arrives on the stream as each ticker's daily bar lands (17:00-18:15
  // ET), as a price only. The cells that depend on it (Med%, Med Z, Cheap, Rank) come from
  // the matrix payload, so when the stream is ahead of the payload this tab re-reads it, once
  // per burst of frames (the first frame arms one timer; frames arriving before it fires are
  // covered by it), not more often than every few minutes and not more than a handful of
  // times between two session changes (see utils/session.js). The same re-read the close uses.
  const openMatrixTickers = React.useMemo(() => {
    const out = new Set();
    activeListNames.forEach((name) => (lists[name] || []).forEach((t) => out.add(t)));
    return [...out];
  }, [activeListNames, lists]);
  const officialRereadRef = useRef({ timer: null, count: 0, lastAt: null });
  useEffect(() => {
    officialRereadRef.current.count = 0;             // a new session starts a new budget
    officialRereadRef.current.lastAt = null;
  }, [session]);
  useEffect(() => () => clearTimeout(officialRereadRef.current.timer), []);
  // What the timer checks when it fires: the newest state, not the state it was armed with.
  const officialLatestRef = useRef(null);
  officialLatestRef.current = { wsPrices, yields, tickers: openMatrixTickers };
  useEffect(() => {
    const st = officialRereadRef.current;
    if (st.timer || session === 'regular') return;
    // Only frames received after the last re-read was requested count: until its payload lands,
    // the old one still looks behind the stream, and that must not arm another.
    const since = st.lastAt ?? 0;
    if (officialAheadOfPayload(wsPrices, yields, openMatrixTickers, since).length === 0) return;
    const delay = officialRereadDelayMs({ now: Date.now(), lastAt: st.lastAt, count: st.count });
    if (delay == null) return;
    st.timer = setTimeout(() => {
      st.timer = null;
      const cur = officialLatestRef.current;
      // Caught up in the meantime (a reload of the lists, another re-read): nothing to ask for.
      if (officialAheadOfPayload(cur.wsPrices, cur.yields, cur.tickers, since).length === 0) return;
      st.count += 1;
      st.lastAt = Date.now();
      clearClientSpreadCaches();
      setSessionRefresh((n) => n + 1);
    }, delay);
  }, [wsPrices, yields, openMatrixTickers, session]);

  // Throttle livePrices updates to max once per 3 seconds for responsive live updates
  const throttledLivePrices = useThrottledValue(livePrices, 1000);
  const latestLivePricesRef = useRef(throttledLivePrices);
  latestLivePricesRef.current = throttledLivePrices;

  // Use deferred values for heavy data to keep UI responsive
  const deferredSpreadMatrices = useDeferredValue(spreadMatrices);

  // (scalingFactors state moved up before save effect)

  const [plotDialog, setPlotDialog] = useState({
    open: false,
    ticker1: '',
    ticker2: '',
    data: null,
    loading: false,  // Loading state for async data fetch
    timeRange: '6M',
    granularity: '30m',  // Track granularity for this dialog
    listName: null   // Track which list was clicked for relative percentile
  });

  // Prefetch cache for hover data preloading (Phase 5.1)
  const prefetchCacheRef = useRef({});
  const prefetchTimeoutRef = useRef(null);

  // WebSocket connection for live price updates
  const { subscribeToPrices, isConnected } = useWebSocket();

  useEffect(() => {
    // Load initial data - just fetch lists, don't auto-load matrices.
    // (The ticker universe is fetched by the ticker tape's settings dialog.)
    dispatch(fetchUserLists());
    // Pre-fetch alert subscriptions so the 🔔 icon on each spread chart can
    // render in its correct color (yellow=subscribed) from first paint,
    // not only after the user opens the alerts dialog once.
    dispatch(fetchSubscriptions());
  }, [dispatch]);

  // Subscribe matrix tickers to WebSocket for live price updates
  // NOTE: Do NOT add matrix tickers to ticker tape - they should be separate
  useEffect(() => {
    if (!isConnected || activeListNames.length === 0) return;

    // Collect all unique tickers from active lists
    const allTickers = new Set();
    activeListNames.forEach(listName => {
      const tickers = lists[listName] || [];
      tickers.forEach(t => allTickers.add(t));
    });

    if (allTickers.size > 0) {
      // Subscribe to live prices via WebSocket (for matrix only). Also at every
      // session change: the server answers a subscribe with a snapshot, which
      // picks up a first trade that arrived a moment before this clock reached
      // 09:30 and was not applied.
      subscribeToPrices([...allTickers]);
      // DO NOT add to tickerTape - ticker tape has its own fixed tickers
    }
  }, [isConnected, activeListNames, lists, subscribeToPrices, session]);

  // NO auto-assign - let user select lists manually for faster initial load

  // Ratios that affect one list's matrix: pairs inside the list, not at the
  // default of 1. A ratio change then refetches only the lists it touches.
  const listScaling = useCallback((listName) => {
    const inList = new Set(lists[listName] || []);
    const out = {};
    for (const key of Object.keys(scalingFactors)) {
      const r = scalingFactors[key];
      const [a, b] = key.split('|');
      if (r && r !== 1 && inList.has(a) && inList.has(b)) out[key] = r;
    }
    return out;
  }, [lists, scalingFactors]);

  // Track in-flight requests to prevent duplicates
  const pendingRequestsRef = React.useRef(new Set());

  // Fetch matrix data for active lists. Each stored payload records the
  // settings it was fetched for (requestSig) and which curve it carries
  // (spreadType), so a list refetches exactly when what it needs differs —
  // granularity, lookback, its own ratios, or (for YTC/YTM) the curve.
  useEffect(() => {
    if (activeListNames.length === 0) return;

    const neededCurve = CURVE_FIELD_BY_SPREAD_TYPE[spreadType] || null;
    const timeoutId = setTimeout(() => {
      activeListNames.forEach(listName => {
        const tickers = lists[listName] || [];
        if (tickers.length < 2) return;
        const sf = listScaling(listName);
        // sessionRefresh: bumped once after the regular session ends, so every
        // open list refetches (see the session effect above).
        const requestSig = `${tickers.length}:${selectedGranularity}:${selectedLookbackDays}:${scalingSig(sf)}:${sessionRefresh}`;
        const have = matrixWithLevels[listName];
        const curveOk = !neededCurve || CURVE_FIELD_BY_SPREAD_TYPE[have?.spreadType] === neededCurve;
        if (have && have.requestSig === requestSig && curveOk) return;

        const requestKey = `${listName}:${requestSig}:${neededCurve || ''}`;
        if (pendingRequestsRef.current.has(requestKey)) return;
        pendingRequestsRef.current.add(requestKey);
        setLoadingLists(prev => ({ ...prev, [listName]: true }));
        dispatch(fetchMatrixWithLevels({
          spreadType,
          tickers,
          granularity: selectedGranularity,
          listName,
          lookbackDays: selectedLookbackDays,
          scalingFactors: sf,
          requestSig,
        })).finally(() => {
          pendingRequestsRef.current.delete(requestKey);
          setLoadingLists(prev => ({ ...prev, [listName]: false }));
        });
      });
    }, 100); // Increased debounce slightly

    return () => clearTimeout(timeoutId);
  }, [activeListNames, lists, selectedGranularity, selectedLookbackDays, dispatch, matrixWithLevels, listScaling, spreadType, sessionRefresh]);

  // Δ Med%: each open list's Med% at the reference close. Requested only once
  // the list's matrix has arrived, so it never competes with the matrix build.
  const medPercReference = useSelector((state) => state.spreads.medPercReference, shallowEqual);
  const [medRefTick, setMedRefTick] = useState(0);
  const medRefTickMissedRef = useRef(false);
  useEffect(() => {
    // Nobody reads Δ Med% in a hidden tab: skip the tick there, and catch up
    // once when it is shown again (the session may have rolled meanwhile).
    const id = setInterval(() => {
      if (document.hidden) { medRefTickMissedRef.current = true; return; }
      setMedRefTick(t => t + 1);
    }, MED_REF_REFRESH_MS);
    const onVisibility = () => {
      if (document.hidden || !medRefTickMissedRef.current) return;
      medRefTickMissedRef.current = false;
      setMedRefTick(t => t + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const pendingMedRefRef = useRef(new Set());
  // Settings the reference must match to be shown; the tick is left out so a
  // periodic refresh keeps the old numbers on screen until the new ones land.
  const medRefBaseKey = useCallback(
    (listName) => `${(lists[listName] || []).length}:${selectedGranularity}:${selectedLookbackDays}:${deltaSessionsBack}:${scalingSig(listScaling(listName))}`,
    [lists, selectedGranularity, selectedLookbackDays, deltaSessionsBack, listScaling]
  );
  useEffect(() => {
    activeListNames.forEach(listName => {
      const tickers = lists[listName] || [];
      if (tickers.length < 2 || !matrixWithLevels[listName]) return;
      const requestKey = `${medRefBaseKey(listName)}:${medRefTick}`;
      if (medPercReference[listName]?.requestKey === requestKey) return;
      const inflight = `${listName}|${requestKey}`;
      if (pendingMedRefRef.current.has(inflight)) return;
      pendingMedRefRef.current.add(inflight);
      dispatch(fetchMedPercReference({
        listName,
        requestKey,
        tickers,
        granularity: selectedGranularity,
        lookbackDays: selectedLookbackDays,
        scalingFactors: listScaling(listName),
        sessionsBack: deltaSessionsBack,
      })).finally(() => pendingMedRefRef.current.delete(inflight));
    });
  }, [activeListNames, lists, matrixWithLevels, medPercReference, medRefBaseKey, medRefTick, selectedGranularity, selectedLookbackDays, listScaling, deltaSessionsBack, dispatch]);

  const referenceFor = useCallback((listName) => {
    const entry = listName ? medPercReference[listName] : null;
    if (!entry || !entry.requestKey.startsWith(`${medRefBaseKey(listName)}:`)) return null;
    return entry;
  }, [medPercReference, medRefBaseKey]);

  // Use startTransition for instant button response
  const handleSpreadTypeChange = useCallback((event, newSpreadType) => {
    if (newSpreadType !== null) {
      // Use startTransition so button clicks register instantly
      startTransition(() => {
        setSpreadType(newSpreadType);
      });
    }
  }, []);

  // Handle granularity change - clears matrix cache to force refetch
  const handleGranularityChange = useCallback((event, newGranularity) => {
    if (newGranularity !== null && newGranularity !== selectedGranularity) {
      dispatch(setGranularity(newGranularity));
      // Clear prefetch cache so new clicks fetch data with new granularity
      prefetchCacheRef.current = {};
      toast.success(`Granularity changed to ${newGranularity}`, {
        icon: '⏱️',
        duration: 2000
      });
      // Matrix data will be refetched automatically due to granularity change detection
    }
  }, [dispatch, selectedGranularity]);

  // Handle lookback period change
  // This affects both matrix percentiles AND chart data when clicking a cell.
  // Matrix will refetch with new lookback period for dynamic percentile calculation.
  const handleLookbackChange = useCallback((event, newLookback) => {
    if (newLookback !== null && newLookback !== selectedLookbackDays) {
      dispatch(setLookbackDays(newLookback));
      // Clear prefetch cache so new clicks fetch data with new lookback period
      prefetchCacheRef.current = {};
      toast.success(`Lookback set to ${newLookback === 90 ? '3 months' : newLookback === 180 ? '6 months' : '1 year'} - matrix updating...`, {
        icon: '📊',
        duration: 2000
      });
    }
  }, [dispatch, selectedLookbackDays]);

  // Check data freshness on mount
  useEffect(() => {
    dispatch(checkDataFreshness());
  }, [dispatch]);

  // Sync menu state
  const [syncMenuAnchor, setSyncMenuAnchor] = useState(null);
  const syncMenuOpen = Boolean(syncMenuAnchor);

  // Handle data sync with scope: 'current', 'user', or 'all'
  const handleDataSync = useCallback((scope = 'all') => {
    setSyncMenuAnchor(null); // Close menu

    // If data is already fresh, don't call the API
    if (dataFreshness && !dataFreshness.needsUpdate) {
      toast.success('Data is already up-to-date!', { id: 'data-sync', duration: 3000 });
      return;
    }

    // Collect tickers for 'current' scope
    let scopeTickers = null;
    if (scope === 'current') {
      const tickerSet = new Set();
      activeListNames.forEach(name => {
        (lists[name] || []).forEach(t => tickerSet.add(t));
      });
      scopeTickers = [...tickerSet];
      if (scopeTickers.length === 0) {
        toast.error('No tickers in current matrices', { id: 'data-sync', duration: 3000 });
        return;
      }
    }

    const scopeLabel = scope === 'current' ? 'current matrices' : scope === 'user' ? 'your saved lists' : 'all spreads';
    toast.loading(`Starting sync of ${scopeLabel}...`, { id: 'data-sync' });

    // The server answers at once; the run itself is followed by the status
    // poll below, which also reports how it ended.
    dispatch(syncData({ scope, tickers: scopeTickers }))
      .unwrap()
      .then((result) => {
        if (result.state === 'running') {
          const since = result.startedAt ? new Date(result.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
          toast(result.joined
            ? `A sync is already running (${result.scopeLabel}, started ${since} by ` +
              `${syncStarter(result.startedBy)}); following it`
            : `Syncing ${scopeLabel} in the background (usually 15-40 min)`,
          { id: 'data-sync', icon: '🔄', duration: 5000 });
          return;
        }
        toast.success(result.cooldown ? result.message : 'Data is already up-to-date!', { id: 'data-sync', duration: 4000 });
        dispatch(checkDataFreshness());
      })
      .catch((error) => {
        toast.error(`Sync failed: ${error}`, { id: 'data-sync', duration: 5000 });
      });
  }, [dispatch, dataFreshness, activeListNames, lists]);

  // Pick up a sync already running: started in another tab or by another
  // user, the nightly run, or this tab before a reload.
  useEffect(() => {
    dispatch(fetchSyncStatus());
  }, [dispatch]);

  // While a sync runs, poll it. When the one this tab followed ends, say how,
  // and refetch what it changed: the open matrices (the server cleared its
  // matrix and Δ Med% caches before reporting completion) and the freshness.
  const followedSyncJobRef = useRef(null);
  useEffect(() => {
    if (!syncJob) return undefined;
    if (syncJob.state === 'running') {
      followedSyncJobRef.current = syncJob.jobId;
      const id = setTimeout(() => dispatch(fetchSyncStatus()), document.hidden ? SYNC_POLL_HIDDEN_MS : SYNC_POLL_MS);
      return () => clearTimeout(id);
    }
    if (!followedSyncJobRef.current) return undefined;
    followedSyncJobRef.current = null;
    const by = syncJob.external ? ` (started by ${syncStarter(syncJob.startedBy)})` : '';
    if (syncJob.state === 'completed' && syncJob.skipped) {
      toast.success('Data is already up-to-date!', { id: 'data-sync', duration: 3000 });
    } else if (syncJob.exitUnknown) {
      // A run this backend did not start (the nightly, an orphan): it saw the
      // run end but not its exit status, so report the data, not success.
      const ended = `The ${syncJob.scopeLabel} sync started by ${syncStarter(syncJob.startedBy)} has finished`;
      const after = syncJob.afterSync;
      if (after && !after.needsUpdate) {
        toast.success(`${ended}; data is up to date`, { id: 'data-sync', duration: 5000 });
      } else if (after) {
        toast(`${ended}, but some data is still behind`, { id: 'data-sync', icon: '⚠️', duration: 8000 });
      } else {
        toast(ended, { id: 'data-sync', icon: 'ℹ️', duration: 5000 });
      }
    } else if (syncJob.state === 'completed') {
      toast.success(`Synced ${syncJob.scopeLabel} successfully${by}!`, { id: 'data-sync', duration: 4000 });
    } else if (syncJob.state === 'completed_with_errors') {
      const n = syncJob.writeFailures;
      toast(`Sync finished with errors: ${n != null ? `${n} ClickHouse write${n === 1 ? '' : 's'} failed` : 'some writes failed'}; ` +
        'some spreads may be missing until the next sync', { id: 'data-sync', icon: '⚠️', duration: 10000 });
    } else {
      toast.error(`Sync failed: ${syncJob.error || 'unknown error'}`, { id: 'data-sync', duration: 8000 });
    }
    if (syncJob.ran) {
      clearClientSpreadCaches();
      dispatch(syncFinished());
    }
    dispatch(checkDataFreshness());
    return undefined;
  }, [syncJob, dispatch]);

  // Compute sync button severity from freshness data
  const syncButtonState = useMemo(() => {
    if (!dataFreshness) return { color: 'inherit', severity: 'unknown' };
    if (!dataFreshness.needsUpdate) return { color: 'success', severity: 'fresh' };
    // Find max lag across all granularities
    const maxLag = Math.max(
      ...Object.values(dataFreshness.status || {}).map(s =>
        Math.max(s.yieldsLagHours || 0, s.spreadsLagHours || 0)
      ), 0
    );
    if (maxLag > 24) return { color: 'error', severity: 'stale' };
    return { color: 'warning', severity: 'behind' };
  }, [dataFreshness]);

  // Check freshness again after sync
  const handleCheckFreshness = useCallback(() => {
    dispatch(checkDataFreshness());
  }, [dispatch]);

  // Handle list selection change for a box
  const handleBoxListChange = useCallback((boxId, newListName) => {
    setMatrixBoxes(prevBoxes =>
      prevBoxes.map(box =>
        box.id === boxId ? { ...box, listName: newListName } : box
      )
    );
  }, []);

  // Remove a matrix box
  const handleRemoveBox = useCallback((boxId) => {
    setMatrixBoxes(prevBoxes => {
      if (prevBoxes.length <= 1) {
        // Keep at least one box, just clear it
        return prevBoxes.map(box =>
          box.id === boxId ? { ...box, listName: null } : box
        );
      }
      return prevBoxes.filter(box => box.id !== boxId);
    });
    setMaximizedBoxId(prev => (prev === boxId ? null : prev));
  }, []);

  // The active box must always exist (a removed or stale id falls back to the first).
  useEffect(() => {
    if (!matrixBoxes.some(b => b.id === activeBoxId) && matrixBoxes.length > 0) {
      setActiveBoxId(matrixBoxes[0].id);
    }
  }, [matrixBoxes, activeBoxId]);

  // Add a new matrix box, optionally with a list already chosen. It becomes the
  // active box, and a maximized view is restored so the new box is visible.
  const addBox = useCallback((listName = null) => {
    if (matrixBoxes.length >= MAX_BOXES) {
      setSnackbar({
        open: true,
        message: `Maximum ${MAX_BOXES} matrices allowed`,
        severity: 'warning'
      });
      return;
    }
    setMatrixBoxes(prevBoxes => [...prevBoxes, { id: nextBoxId, listName }]);
    setActiveBoxId(nextBoxId);
    setMaximizedBoxId(null);
    setNextBoxId(prev => prev + 1);
  }, [matrixBoxes.length, nextBoxId]);
  const handleAddBox = useCallback(() => addBox(null), [addBox]);

  // Lists panel: load into the active box.
  const handleSelectListFromPanel = useCallback((listName) => {
    handleBoxListChange(activeBoxId, listName);
  }, [activeBoxId, handleBoxListChange]);

  // Lists picked in the side panel (Ctrl/Shift+click) for "Compare sectors".
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const handleCompareSectors = useCallback(() => {
    if (selectedForCompare.length < 2) {
      toast('Pick two or more lists in the side panel first: Ctrl+click or Shift+click', { icon: '👈' });
      setSidePanelOpen(true);
      return;
    }
    setCompareOpen(true);
  }, [selectedForCompare.length]);

  // A list was deleted by an edit (merged away, or emptied): matrices that were
  // showing it follow it to the list it went into, or go blank.
  const handleListRemoved = useCallback((removed, replacement) => {
    setMatrixBoxes(prev => prev.map(b => (b.listName === removed ? { ...b, listName: replacement || null } : b)));
    setSelectedForCompare(prev => prev.filter(n => n !== removed));
  }, []);
  const listEditing = useListEditing({ lists: storedLists, onListRemoved: handleListRemoved });

  const handleToggleMaximize = useCallback((boxId) => {
    setMaximizedBoxId(prev => {
      const next = prev === boxId ? null : boxId;
      // Hidden boxes get this snapshot instead of the live feed, so their memo
      // bails out and they cost nothing while another box is maximized.
      frozenLivePricesRef.current = next == null ? null : latestLivePricesRef.current;
      return next;
    });
    setActiveBoxId(boxId);
  }, []);

  // Esc restores a maximized matrix (unless the chart dialog is open — it owns Esc then).
  useEffect(() => {
    if (maximizedBoxId == null || plotDialog.open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        frozenLivePricesRef.current = null;
        setMaximizedBoxId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximizedBoxId, plotDialog.open]);

  // Prefetch data on cell hover (Phase 5.1) - starts fetching before click
  // Uses sorted cache key so A-B and B-A share the same cache entry
  const handleCellHover = useCallback((ticker1, ticker2) => {
    // Clear any pending prefetch
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }

    // Debounce 150ms - prefetch starts quickly for snappier feel
    prefetchTimeoutRef.current = setTimeout(async () => {
      // Use sorted key so A-B and B-A share cache (includes lookback days)
      const cacheKey = getSortedCacheKey(ticker1, ticker2, selectedGranularity, selectedLookbackDays);
      const [sortedT1, sortedT2] = [ticker1, ticker2].sort();

      // Skip if already cached or currently fetching
      if (prefetchCacheRef.current[cacheKey]) return;

      // Mark as fetching (prevent duplicate requests)
      prefetchCacheRef.current[cacheKey] = { fetching: true };

      try {
        // Always fetch with sorted order (A-B) to store canonical data
        const response = await dispatch(fetchSpreads({
          ticker1: sortedT1,
          ticker2: sortedT2,
          granularity: selectedGranularity,
          historical: true,
          days: selectedLookbackDays
        }));

        const historicalData = response.payload?.historical || [];
        addToLRUCache(prefetchCacheRef.current, cacheKey, { data: historicalData, timestamp: Date.now() });
      } catch {
        // Failed prefetch - remove marker so click can retry
        delete prefetchCacheRef.current[cacheKey];
      }
    }, 150);
  }, [dispatch, selectedGranularity, selectedLookbackDays]);

  // Cancel prefetch on hover leave
  const handleCellHoverEnd = useCallback(() => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
    }
  }, []);

  const handleCellClick = async (ticker1, ticker2, listName) => {
    // Use sorted cache key so A-B and B-A share cache (includes lookback days)
    const cacheKey = getSortedCacheKey(ticker1, ticker2, selectedGranularity, selectedLookbackDays);
    const [sortedT1, sortedT2] = [ticker1, ticker2].sort();
    const needsInvert = sortedT1 !== ticker1; // B-A needs inversion from cached A-B data
    const cached = prefetchCacheRef.current[cacheKey];

    // Determine initial time range based on selected lookback days
    const initialTimeRange = lookbackDaysToTimeRange(selectedLookbackDays);

    // Check if we have prefetched data ready
    if (cached && cached.data && !cached.fetching) {
      // Instant open with cached data! Invert if B-A order requested
      const displayData = needsInvert ? invertHistoricalData(cached.data) : cached.data;
      setPlotDialog({
        open: true,
        ticker1,
        ticker2,
        data: displayData,
        loading: false,
        timeRange: initialTimeRange,
        granularity: selectedGranularity,
        listName
      });
      return;
    }

    // Open dialog immediately with loading state for instant feedback
    setPlotDialog({
      open: true,
      ticker1,
      ticker2,
      data: null,
      loading: true,
      timeRange: initialTimeRange,
      granularity: selectedGranularity,
      listName
    });

    // Fetch historical spread data asynchronously (always with sorted order for caching)
    try {
      const response = await dispatch(fetchSpreads({
        ticker1: sortedT1,
        ticker2: sortedT2,
        granularity: selectedGranularity,
        historical: true,
        days: selectedLookbackDays
      }));

      const historicalData = response.payload?.historical || [];

      if (historicalData.length === 0) {
        console.warn(`No historical data returned for ${sortedT1} vs ${sortedT2}`, response.payload);
      }

      // Update cache with canonical A-B data (for LRU eviction)
      addToLRUCache(prefetchCacheRef.current, cacheKey, { data: historicalData, timestamp: Date.now() });

      // Invert data for display if B-A order was requested
      const displayData = needsInvert ? invertHistoricalData(historicalData) : historicalData;

      // Update dialog with data
      setPlotDialog(prev => ({
        ...prev,
        data: displayData,
        loading: false
      }));
    } catch (error) {
      console.error(`fetchSpreads error for ${ticker1} vs ${ticker2}:`, error);
      toast.error(`Failed to load spread data for ${ticker1} vs ${ticker2}`);
      setPlotDialog(prev => ({
        ...prev,
        data: [],
        loading: false
      }));
    }
  };

  const handleClosePlot = () => {
    setPlotDialog({
      open: false,
      ticker1: '',
      ticker2: '',
      data: null,
      loading: false,
      timeRange: '6M',
      granularity: '30m',
      listName: null
    });
  };

  // Active scaled pair for matrix cell update (only when dialog open and s != 1)
  // Compute relative percentile data for the selected ticker
  const relativePercentileData = React.useMemo(() => {
    if (!plotDialog.listName || !plotDialog.ticker1) return [];

    const listTickers = lists[plotDialog.listName] || [];
    const matrixData = spreadMatrices[plotDialog.listName];

    if (!matrixData || listTickers.length === 0) return [];

    // Get percentile of ticker1 vs each ticker in list (INCLUDING ticker1 itself, matches notebook)
    // Notebook diagonal: spread[i][i] is all-zeros series; percentileofscore(zeros, 0) = 50
    return listTickers
      .map(ticker => {
        if (ticker === plotDialog.ticker1) {
          return { ticker, percentile: 50 }; // Diagonal entry (matches notebook)
        }
        const cellData = matrixData[plotDialog.ticker1]?.[ticker];
        let percentile = null;

        if (cellData) {
          const spreadField = spreadType === 'dollar' || spreadType === 'percentile'
            ? cellData.dollar_spread
            : spreadType === 'ytc'
            ? cellData.ytc_spread
            : cellData.ytm_spread || cellData.spot_yield_spread;

          percentile = spreadField?.percentile ?? null;
        }

        return { ticker, percentile };
      })
      .filter(d => d.percentile !== null)
      .sort((a, b) => a.ticker.localeCompare(b.ticker));
  }, [plotDialog.listName, plotDialog.ticker1, lists, spreadMatrices, spreadType]);

  // Compute live data for the selected ticker pair (for chart live point)
  const chartLiveData = React.useMemo(() => {
    if (!plotDialog.open || !plotDialog.ticker1 || !plotDialog.ticker2) return null;

    const t1Data = throttledLivePrices[plotDialog.ticker1];
    const t2Data = throttledLivePrices[plotDialog.ticker2];

    if (!t1Data || !t2Data) return null;

    return {
      // What the two prices are, so the chart can call its last point LIVE only
      // when it is a trade rather than a close.
      kind: t1Data.kind === t2Data.kind ? (t1Data.kind || null) : 'mixed',
      ticker1: {
        price: t1Data.price,
        cleanPrice: t1Data.cleanPrice,
        ytm: t1Data.ytm,
        ytc: t1Data.ytc,
        currentYield: t1Data.currentYield
      },
      ticker2: {
        price: t2Data.price,
        cleanPrice: t2Data.cleanPrice,
        ytm: t2Data.ytm,
        ytc: t2Data.ytc,
        currentYield: t2Data.currentYield
      }
    };
  }, [plotDialog.open, plotDialog.ticker1, plotDialog.ticker2, throttledLivePrices]);

  // The chart's own granularity / lookback can differ from the matrix's once the
  // user changes them in the dialog. The matrix cell's band then describes a
  // different window, so fetch the band for the chart's settings instead.
  const dialogDays = TIME_RANGE_DAYS[plotDialog.timeRange] || 180;
  const dialogMatchesMatrix = plotDialog.granularity === selectedGranularity && dialogDays === selectedLookbackDays;
  const dialogRatio = plotDialog.open ? getScaleFactor(plotDialog.ticker1, plotDialog.ticker2) : 1;
  const bandsKey = (plotDialog.open && plotDialog.ticker1 && plotDialog.ticker2 && !dialogMatchesMatrix)
    ? `${plotDialog.ticker1}|${plotDialog.ticker2}|${plotDialog.granularity}|${dialogDays}|${dialogRatio}`
    : null;
  const [dialogBands, setDialogBands] = useState({ key: null, bands: null });
  useEffect(() => {
    if (!bandsKey) return undefined;
    const [t1, t2, gran, days, ratio] = bandsKey.split('|');
    let cancelled = false;
    const params = new URLSearchParams({ ticker1: t1, ticker2: t2, granularity: gran, days, ratio });
    fetch(addUserParam(`${getApiUrl()}/spreads/bands?${params}`), { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`bands ${r.status}`))))
      .then(b => {
        if (!cancelled) setDialogBands({ key: bandsKey, bands: { dollar: b.dollar, ytc: b.ytc, ytm: b.ytm } });
      })
      .catch(() => {
        if (!cancelled) setDialogBands({ key: bandsKey, bands: null });
      });
    return () => { cancelled = true; };
  }, [bandsKey]);

  // Per-spread-type (p5, mean, p95) bands for the chart reference lines,
  // taken from the same matrix cell the user clicked.
  //
  // This replaces a `chartLevels` memo that read `cellData.<type>.levels` — a
  // field the backend deletes from every cell before responding
  // (routes/spreads.js, the strip-levels pass), so it always produced empty
  // arrays and the charts silently fell back to recomputing bands from a
  // heavily downsampled series. Reading the p5/mean/p95 triples the payload
  // actually carries costs nothing extra and makes chart and cell agree.
  const chartBands = React.useMemo(() => {
    if (bandsKey) return dialogBands.key === bandsKey ? dialogBands.bands : null;
    if (!plotDialog.listName || !plotDialog.ticker1 || !plotDialog.ticker2) return null;

    const matrixData = spreadMatrices[plotDialog.listName];
    if (!matrixData) return null;

    const cellData = matrixData[plotDialog.ticker1]?.[plotDialog.ticker2];
    if (!cellData) return null;

    const band = (field) => {
      const f = cellData[field];
      if (!f || f.avg == null) return null;
      return { p5: f.p5, mean: f.avg, p95: f.p95 };
    };

    return {
      dollar: band('dollar_spread'),
      ytc: band('ytc_spread'),
      // ytm_spread and spot_yield_spread are value-identical in every stored row.
      ytm: band('ytm_spread') || band('spot_yield_spread'),
    };
  }, [plotDialog.listName, plotDialog.ticker1, plotDialog.ticker2, spreadMatrices, bandsKey, dialogBands]);

  // Handle time range change in dialog - refetches data with new lookback
  const handleTimeRangeChange = async (newTimeRange) => {
    const { ticker1, ticker2, granularity } = plotDialog;
    if (!ticker1 || !ticker2) return;

    const days = TIME_RANGE_DAYS[newTimeRange] || 180;

    // Update state immediately for responsive UI
    setPlotDialog(prev => ({ ...prev, timeRange: newTimeRange, loading: true }));

    // Fetch new data with updated lookback
    const [sortedT1, sortedT2] = [ticker1, ticker2].sort();
    const needsInvert = sortedT1 !== ticker1;

    try {
      const response = await dispatch(fetchSpreads({
        ticker1: sortedT1,
        ticker2: sortedT2,
        granularity,
        historical: true,
        days
      }));

      const historicalData = response.payload?.historical || [];
      const displayData = needsInvert ? invertHistoricalData(historicalData) : historicalData;

      setPlotDialog(prev => ({
        ...prev,
        data: displayData,
        loading: false
      }));
    } catch (error) {
      toast.error('Failed to load data for new time range');
      setPlotDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle granularity change in dialog - refetches data with new granularity
  const handleDialogGranularityChange = async (newGranularity) => {
    const { ticker1, ticker2, timeRange } = plotDialog;
    if (!ticker1 || !ticker2) return;

    const days = TIME_RANGE_DAYS[timeRange] || 180;

    // Update state immediately for responsive UI
    setPlotDialog(prev => ({ ...prev, granularity: newGranularity, loading: true }));

    // Fetch new data with updated granularity
    const [sortedT1, sortedT2] = [ticker1, ticker2].sort();
    const needsInvert = sortedT1 !== ticker1;

    try {
      const response = await dispatch(fetchSpreads({
        ticker1: sortedT1,
        ticker2: sortedT2,
        granularity: newGranularity,
        historical: true,
        days
      }));

      const historicalData = response.payload?.historical || [];
      const displayData = needsInvert ? invertHistoricalData(historicalData) : historicalData;

      setPlotDialog(prev => ({
        ...prev,
        data: displayData,
        loading: false
      }));
    } catch (error) {
      toast.error('Failed to load data for new granularity');
      setPlotDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle reanalyze button click
  const handleReanalyze = async (listName) => {
    const tickers = lists[listName] || [];
    if (tickers.length < 2) {
      setSnackbar({
        open: true,
        message: 'Need at least 2 tickers to reanalyze',
        severity: 'warning'
      });
      return;
    }

    setReanalyzeListName(listName);
    setSnackbar({
      open: true,
      message: `Starting reanalysis for ${listName}...`,
      severity: 'info'
    });

    try {
      const result = await dispatch(reanalyzeSpreads({
        listName,
        tickers,
        granularity: selectedGranularity
      })).unwrap();

      if (result.skipped) {
        // Data is already fresh or calculation is already running
        const msg = result.reason === 'calculation_in_progress'
          ? 'A spread calculation is already in progress. Please wait.'
          : `Spread data for ${listName} is already up-to-date.`;
        setSnackbar({
          open: true,
          message: msg,
          severity: 'info'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Reanalysis complete for ${listName}. Refreshing data...`,
          severity: 'success'
        });

        // Only refetch matrix data if calculation actually ran
        const sf = listScaling(listName);
        dispatch(fetchMatrixWithLevels({
          spreadType,
          tickers,
          granularity: selectedGranularity,
          listName,
          lookbackDays: selectedLookbackDays,
          scalingFactors: sf,
          requestSig: `${tickers.length}:${selectedGranularity}:${selectedLookbackDays}:${scalingSig(sf)}:${sessionRefresh}`,
        }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Reanalysis failed: ${error}`,
        severity: 'error'
      });
    } finally {
      setReanalyzeListName(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      {/* Ticker Tape at the top */}
      <TickerTape />
      
      <Container maxWidth={false} sx={{ mt: 1, mb: 2, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <ListsPanel
          lists={storedLists}
          tickerTypes={tickerTypes}
          excludedFlags={excludedFlags}
          selectedLists={selectedForCompare}
          onSelectedListsChange={setSelectedForCompare}
          editing={listEditing}
          defaultListNames={defaultListNames}
          open={sidePanelOpen}
          onToggle={() => setSidePanelOpen(o => !o)}
          boxes={matrixBoxes}
          activeBoxId={activeBoxId}
          onSelectList={handleSelectListFromPanel}
          onOpenInNewBox={addBox}
          canAddBox={matrixBoxes.length < MAX_BOXES}
          topOffset={72}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {/* Spread Type Toggle - instant response */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Spread:
                  </Typography>
                  <ToggleButtonGroup
                    value={spreadType}
                    exclusive
                    onChange={handleSpreadTypeChange}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        transition: 'none',
                        px: 1.5,
                        py: 0.5,
                      }
                    }}
                  >
                    <ToggleButton value="dollar">$</ToggleButton>
                    <ToggleButton value="ytc">YTC</ToggleButton>
                    <ToggleButton value="spot-yield">YTM</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Granularity Toggle */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Granularity:
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedGranularity}
                    exclusive
                    onChange={handleGranularityChange}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        transition: 'none',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="30m">30m</ToggleButton>
                    <ToggleButton value="1h">1h</ToggleButton>
                    <ToggleButton value="1d">1d</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Lookback Period Toggle */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Lookback:
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedLookbackDays}
                    exclusive
                    onChange={handleLookbackChange}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        transition: 'none',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'success.main',
                          color: 'success.contrastText',
                          '&:hover': {
                            backgroundColor: 'success.dark',
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value={90}>3M</ToggleButton>
                    <ToggleButton value={180}>6M</ToggleButton>
                    <ToggleButton value={365}>1Y</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Δ Med% comparison period */}
                <Tooltip title="ΔMed column: change in Med% since the close this many sessions before the one shown">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      ΔMed vs:
                    </Typography>
                    <ToggleButtonGroup
                      value={deltaSessionsBack}
                      exclusive
                      onChange={(_, v) => v != null && setDeltaSessionsBack(v)}
                      size="small"
                      sx={{ '& .MuiToggleButton-root': { transition: 'none', px: 1.25, py: 0.5 } }}
                    >
                      {DELTA_OPTIONS.map(o => (
                        <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </Tooltip>

                {/* Add Matrix Button */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBox}
                  disabled={matrixBoxes.length >= MAX_BOXES}
                  sx={{ minWidth: 'auto' }}
                >
                  Add Matrix
                </Button>

                {/* Compare sectors: lists picked in the side panel, side by side */}
                <Tooltip describeChild title="Compare the median yields of the lists you picked in the side panel (Ctrl/Shift+click to select several)">
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<CompareArrowsIcon />}
                    onClick={handleCompareSectors}
                    sx={{ minWidth: 'auto', fontWeight: 700 }}
                  >
                    Compare sectors{selectedForCompare.length > 1 ? ` (${selectedForCompare.length})` : ''}
                  </Button>
                </Tooltip>

                {/* Burger Menu */}
                <BurgerMenu
                  selectedLists={activeListNames}
                  onListsChange={() => {}} // No longer used directly
                />
              </Box>

              {/* Security-type filters: the same three the bonds dashboard has. A view only. */}
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ width: '100%', mt: -0.5, order: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
                  Exclude:
                </Typography>
                {TYPE_FILTERS.map(f => (
                  <Tooltip
                    key={f.key}
                    describeChild
                    title={`${f.hint}. Hides them from every list's matrix${tickerTypes ? ` (${typeCounts[f.key]} in your lists)` : ''}; the lists themselves are not changed.`}
                  >
                    <FormControlLabel
                      sx={{ m: 0, mr: 1 }}
                      control={
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5 }}
                          checked={!!typeFilters[f.key]}
                          onChange={(e) => handleTypeFilter(f.key, e.target.checked)}
                          disabled={!tickerTypes}
                        />
                      }
                      label={<Typography variant="body2">{f.label}{tickerTypes ? ` (${typeCounts[f.key]})` : ''}</Typography>}
                    />
                  </Tooltip>
                ))}
              </Box>

              {/* Sync Spreads Button with Dropdown - Right Side */}
              <Box sx={{ ml: 'auto', order: 2 }}>
                <Tooltip
                  title={
                    dataSyncing && syncJob?.state === 'running' ? (
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Sync in progress
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {syncJob.scopeLabel} · started {syncJob.startedAt ? new Date(syncJob.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '?'}
                          {' '}by {syncStarter(syncJob.startedBy)}
                        </Typography>
                        {syncJob.progress?.label && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {syncJob.progress.label}
                          </Typography>
                        )}
                      </Box>
                    ) : dataFreshness ? (
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Data Status:
                        </Typography>
                        {Object.entries(dataFreshness.status || {}).map(([gran, status]) => (
                          <Typography key={gran} variant="caption" sx={{ display: 'block' }}>
                            {gran}: {status.upToDate
                              ? '✓ Up-to-date'
                              : status.staleTickers != null
                                ? `⚠ ${status.staleTickers}/${status.tickers} tickers behind (up to ${status.yieldsLagHours || 0}h)`
                                : `⚠ ${status.spreadsLagHours || 0}h behind`}
                            {status.deadTickers ? ` · ${status.deadTickers} not trading` : ''}
                            {status.noDividendTickers ? ` · ${status.noDividendTickers} without dividend data (no yields)` : ''}
                          </Typography>
                        ))}
                      </Box>
                    ) : 'Check and sync spread data'
                  }
                >
                  {/* span: a disabled button fires no hover events for the tooltip */}
                  <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={dataSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setSyncMenuAnchor(e.currentTarget)}
                    disabled={dataSyncing}
                    color={syncButtonState.color}
                    sx={{
                      minWidth: 'auto',
                      borderColor: `${syncButtonState.color}.main`,
                      '&:hover': {
                        borderColor: `${syncButtonState.color}.dark`,
                      }
                    }}
                  >
                    {dataSyncing
                      ? `Syncing…${syncJob?.state === 'running' && syncJob.progress?.label ? ` ${syncJob.progress.label}` : ''}`
                      : 'Sync Spreads'}
                    {dataFreshness && !dataSyncing && (
                      syncButtonState.severity === 'fresh' ? (
                        <CheckCircleIcon sx={{ ml: 0.5, fontSize: 16 }} />
                      ) : syncButtonState.severity === 'stale' ? (
                        <ErrorIcon sx={{ ml: 0.5, fontSize: 16 }} />
                      ) : (
                        <WarningIcon sx={{ ml: 0.5, fontSize: 16 }} />
                      )
                    )}
                  </Button>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={syncMenuAnchor}
                  open={syncMenuOpen}
                  onClose={() => setSyncMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => handleDataSync('current')} disabled={dataSyncing}>
                    <ListItemIcon><ViewModuleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Sync Current Matrices"
                      secondary={`${activeListNames.length} list(s) open`}
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleDataSync('user')} disabled={dataSyncing}>
                    <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Sync My Saved Lists"
                      secondary="All your lists"
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleDataSync('all')} disabled={dataSyncing}>
                    <ListItemIcon><PublicIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Sync All Spreads"
                      secondary="All users, all lists"
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Matrix Boxes - dynamic grid sizing based on count */}
        {matrixBoxes.map((box, index) => {
          const listTickers = box.listName ? (lists[box.listName] || []) : [];
          const isBoxLoading = loadingLists[box.listName] || false;
          const isBoxReanalyzing = reanalyzeListName === box.listName;
          const maximizedExists = maximizedBoxId != null && matrixBoxes.some(b => b.id === maximizedBoxId);
          const isMaximized = maximizedExists && box.id === maximizedBoxId;
          const isHidden = maximizedExists && !isMaximized;
          const reference = referenceFor(box.listName);

          // Dynamic grid sizing: 1 box = full width, 2-4 = 2 columns, 5-9 = 3 columns
          // (up to 3 x 3). Three rows split the viewport three ways, with a floor so a
          // matrix stays usable (the page scrolls instead of squashing it).
          const boxCount = matrixBoxes.length;
          const gridSize = (boxCount === 1 || isMaximized) ? 12 : boxCount <= 4 ? 6 : 4;
          const boxHeight = (boxCount === 1 || isMaximized)
            ? 'calc(100vh - 180px)'
            : boxCount === 2
              ? 'calc(50vh - 90px)'  // Half viewport minus header space
              : boxCount <= 6
                ? 'calc(50vh - 100px)'
                : 'max(300px, calc((100vh - 220px) / 3))';

          return (
            <Grid item xs={12} md={gridSize} key={box.id} sx={isHidden ? { display: 'none' } : undefined}>
              <Fade in={true} timeout={200}>
                <div>
                  <MatrixBox
                    boxId={box.id}
                    boxNumber={index + 1}
                    isActive={box.id === activeBoxId}
                    onActivate={setActiveBoxId}
                    isMaximized={isMaximized}
                    onToggleMaximize={handleToggleMaximize}
                    referenceMedPerc={reference?.medPerc?.[spreadType] || null}
                    referenceSession={reference?.referenceSession || null}
                    selectedListName={box.listName}
                    availableLists={lists}
                    defaultListNames={defaultListNames}
                    onListChange={handleBoxListChange}
                    onRemove={handleRemoveBox}
                    onReanalyze={handleReanalyze}
                    spreadType={spreadType}
                    livePrices={isHidden ? (frozenLivePricesRef.current || throttledLivePrices) : throttledLivePrices}
                    matrixData={deferredSpreadMatrices[box.listName]}
                    matrixYields={spreadYields[box.listName]}
                    listTickers={listTickers}
                    isLoading={isBoxLoading}
                    isReanalyzing={isBoxReanalyzing}
                    granularity={selectedGranularity}
                    onCellClick={handleCellClick}
                    onCellHover={handleCellHover}
                    onCellHoverEnd={handleCellHoverEnd}
                    onRemoveTickers={listEditing.requestDeleteTickers}
                    onMoveTickers={listEditing.requestMoveTickers}
                    boxHeight={boxHeight}
                    getScaleFactor={getScaleFactor}
                  />
                </div>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
        </Box>
        </Box>

      <ListEditDialogs editing={listEditing} lists={storedLists} />

      {compareOpen && (
        <Suspense fallback={null}>
          <SectorComparison
            open
            onClose={() => setCompareOpen(false)}
            lists={lists}
            sectorNames={selectedForCompare}
            granularity={selectedGranularity}
            defaultBasis={spreadType === 'ytc' ? 'ytc' : 'ytm'}
            onOpenList={(name) => { handleBoxListChange(activeBoxId, name); setCompareOpen(false); }}
          />
        </Suspense>
      )}

      {/* All Charts Dialog - Shows all v11 charts on one page */}
      <Dialog
        open={plotDialog.open}
        onClose={handleClosePlot}
        maxWidth="xl"
        fullWidth
        TransitionProps={{
          timeout: 0, // Instant open/close - no animation delay
          unmountOnExit: true // Unmount immediately on close
        }}
        PaperProps={{
          sx: {
            height: '90vh',
            transform: 'translateZ(0)', // Force GPU layer
          }
        }}
        disableScrollLock // Prevents body scroll lock layout thrashing
        BackdropProps={{
          sx: { transition: 'none !important' } // No backdrop animation
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {plotDialog.ticker1} vs {plotDialog.ticker2}
            </Typography>
            <IconButton onClick={handleClosePlot} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1, overflow: 'auto' }}>
          {plotDialog.open && (
            <Suspense fallback={
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            }>
              <AllChartsPanel
                data={plotDialog.data || []}
                ticker1={plotDialog.ticker1}
                ticker2={plotDialog.ticker2}
                spreadType={spreadType}
                timeRange={plotDialog.timeRange}
                onTimeRangeChange={handleTimeRangeChange}
                granularity={plotDialog.granularity}
                onGranularityChange={handleDialogGranularityChange}
                loading={plotDialog.loading}
                relativePercentileData={relativePercentileData}
                liveData={chartLiveData}
                bands={chartBands}
                scaleFactor={getScaleFactor(plotDialog.ticker1, plotDialog.ticker2)}
                onScaleFactorChange={(s) => setScaleFactor(plotDialog.ticker1, plotDialog.ticker2, s)}
              />
            </Suspense>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for reanalyze feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default Dashboard;